/**
 * useStateGovLink — external-lookup URL via StateGovSearchLinks catalog.
 *
 * Implements docs/port-design/people-form-port.md §11. The Lookup button in
 * BusinessCipSoSSection / EstateCipCourtSection calls `openInBrowser()` which
 * invokes Teams SDK `app.openLink(url)` — never `window.open` or a plain
 * anchor — to guarantee the URL opens in the user's default browser across
 * Teams desktop / web / mobile hosts. Some Teams clients capture `target=_blank`
 * navigations in the internal webview, which breaks cross-origin lookups on
 * government sites.
 *
 * When the catalog row isn't found or the route isn't yet seeded, the hook
 * returns `link = undefined` and `openInBrowser` is a no-op so callers can
 * render a disabled Lookup button without guarding.
 */
import { useCallback, useEffect, useState } from 'react';
import { app } from '@microsoft/teams-js';
import type {
  StateGovSearchLink,
  StateGovSearchType,
} from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';

export interface UseStateGovLinkResult {
  /** The catalog row or `undefined` if not found / still loading. */
  link: StateGovSearchLink | undefined;
  loading: boolean;
  error: string | null;
  /**
   * Open the catalog URL in the user's default browser via Teams SDK.
   * No-op when `link` is undefined.
   */
  openInBrowser: () => void;
}

/**
 * Hook: look up the StateGovSearchLinks row for the (stateCode, searchType)
 * tuple and return an `openInBrowser` action that goes through
 * `@microsoft/teams-js` `app.openLink()`.
 */
export function useStateGovLink(
  stateCode: string,
  searchType: StateGovSearchType,
): UseStateGovLinkResult {
  const client = useApiClient();
  const [link, setLink] = useState<StateGovSearchLink | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!stateCode || !searchType) {
      setLink(undefined);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const resp = await client.retail.config.stateGovSearchLinks.list(stateCode, searchType);
        if (cancelled) return;
        if (resp.success && resp.data) {
          const found = resp.data.links.find(
            (l) =>
              l.stateCode === stateCode &&
              l.searchType === searchType &&
              l.isActive,
          );
          setLink(found);
        } else {
          setLink(undefined);
        }
      } catch (err) {
        if (cancelled) return;
        setLink(undefined);
        const msg = err instanceof Error ? err.message : 'Failed to load state-gov search links';
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, stateCode, searchType]);

  const openInBrowser = useCallback((): void => {
    if (!link || !link.searchUrl) return;
    try {
      // Teams SDK >= 2.x — `app.openLink` resolves to the host browser on all
      // platforms. Guarded try/catch in case teams-js hasn't been initialized
      // (e.g. local dev harness running outside Teams).
      const maybe = (app as unknown as { openLink?: (url: string) => Promise<void> }).openLink;
      if (typeof maybe === 'function') {
        void maybe(link.searchUrl);
        return;
      }
      // Legacy fallback — should be unreachable in Teams-hosted production.
      if (typeof window !== 'undefined') {
        window.open(link.searchUrl, '_blank', 'noopener,noreferrer');
      }
    } catch {
      if (typeof window !== 'undefined') {
        window.open(link.searchUrl, '_blank', 'noopener,noreferrer');
      }
    }
  }, [link]);

  return { link, loading, error, openInBrowser };
}
